<template>
  <div v-if="isActive" class="modal is-active">
    <div class="modal-card">
      <header class="modal-card-head">
        <h4 class="modal-card-title">
        {{shortType ? `Schedule Short (${$_.capitalize(shortType)}) Item` : 'Schedule Items'}}
      </h4>
      <p class="is-size-5 has-text-weight-normal is-italic has-text-right mr-0 required-label has-text-dark">*required</p>
      </header>

      <section class="modal-card-body is-clipped">
        <o-loading :full-page="false" :active="isLoading" :can-cancel="true" />
        <h3 class="has-text-weight-semibold is-italic is-size-3 has-text-black-bis">
          {{shortType ? 'Add Short Item to*' : 'Add Item(s) to Delivery*'}}
        </h3>
        <div class="is-divider my-2"></div>
        <div class="column mb-3">
          <div class="columns">
            <div class="column is-narrow">
              <div  v-for="option in ['existing','new']" :key="option" class="mb-3">
                <input
                type="radio"
                :id="option"
                :value="option"
                v-model="deliveryType"
                :disabled="option === 'existing' && !deliveries.length"
              />
              <label :for="option" class="has-text-black-bis mr-0"
                :class="option === 'existing' && !deliveries.length ? 'disabled-anchor-tag' : ''">
                {{ option === 'existing' ? 'Existing Delivery' : 'New Delivery' }}
              </label>
              </div>
            </div>
            <div class="column">
              <div class="mb-3">
                <mf-multi-select
                v-model="selectedDelivery"
                :options="deliveries"
                track-by="_id"
                label="name"
                :disabled="deliveryType !== 'existing'"
                placeholder="Choose Delivery"
                :max="1"
              />
              </div>
                <div  v-if="deliveryType === 'new'">
                  <input
                    type="text"
                    v-model="newDeliveryName"
                    placeholder="Enter New Delivery Name"
                    class="input"
                  />
                </div>
            </div>
          </div>
        </div>
        <div class="column px-0 pt-0"  v-if="deliveryType === 'new'">
          <h3 class="has-text-weight-semibold is-italic is-size-3 has-text-black-bis">Delivery Dates</h3>
          <div class="is-divider my-2"></div>
          <div class="columns">
            <div class="column is-6">
               <div class="columns">
                  <div class="column">
                    <div class="field">
                      <label class="label">Ship By* </label>
                      <mf-date
                        :item="card"
                        :input-props="{ kind: '_delivery.deliveryStart', max: '_delivery.deliverBy' }"
                        :is-edit="true"
                      />
                    </div>
                  </div>
                  <div class="column">
                    <div class="field">
                      <label class="label">Deliver By *</label>
                      <mf-date
                        :item="card"
                        :input-props="{ kind: '_delivery.deliverBy', min: '_delivery.deliveryStart' }"
                        :is-edit="true"
                      />
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
       <div class="is-divider my-2"></div>
        <div class="table-container delivery-col mt-3">
          <mf-table
            :tableProps="tableProps"
            :apiMode="false"
            :loadData="loadData"
            :hideGutter="true"
            :checkedRows="selectedItems"
            @checkbox-toggled="selectedRows"
            ref="deliveryItemsTable"
          >
            <template v-slot:qty-not-scheduled="{ rowData: item }">
              <label>{{ item.quantity }}</label>
            </template>

            <template v-slot:qty-to-ship="{ rowData: item }">
              <qty-input
                :value="item.selectedQty"
                @update:value="itemQtyChanged($event, item)"
                :roundTo="4"
                :max="maxQtyAllowed(item)"
                :key="item.selectedQty"
                :isDisabled="item.selectedQty == null || item.selectedQty < 0"
              />
            </template>
          </mf-table>
        </div>
      </section>

      <footer class="modal-card-foot is-justify-content-flex-end">
        <button class="button is-outlined" @click="$emit('close')">Cancel</button>
        <button
          class="button has-background-black-bis"
          v-if="['received', 'load'].includes(shortType)"
          :disabled="isDisabled()"
          @click="createShortDelivery">
          Save Delivery
        </button>
        <button v-else
          class="button has-background-black-bis"
          @click="createDelivery()"
          :disabled="isDisabled()"
        >
          Save Delivery
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import {
  defineComponent, defineAsyncComponent, reactive, toRefs, ref, computed, inject,
} from 'vue';
import { isEmpty, filter, set, cloneDeep, find ,get } from 'lodash';

import MfDate from '@/components/abstract/MfDate.vue';
import { useToast } from 'vue-toastification';
import QtyInput from '@/components/fields/QtyInput.vue';
import ShippingLabel from '@/models/ShippingLabel';
import AddRow from '@/components/utils/AddRow';
import tableDefinition from '@/components/table-cols/createShippingDeliveryCols';
import Shipping from '../../models/Shipping';
  import {  useRouter } from 'vue-router';


const MfTable = defineAsyncComponent(() => import('@/components/table-fields/MfTable.vue'));

export default defineComponent({
  name: 'ScheduleItems',
  components: { MfTable, QtyInput, MfDate },
  props: {
    card: Object,
    masterShipment: Object,
    isActive: Boolean,
    shortType: {
      type: String,
      default: '',
    },
    qtyShortItem: Object,
    parentShipmentId: String,
  },
  setup(props, { emit }) {
    const toast = useToast();
    const emitter = inject('emitter');
    const deliveryItemsTable = ref(null);
    const router = useRouter();

    const state = reactive({
      searchText: '',
      selectedItems: [],
      selectedDelivery: null,
      deliveryType: 'new',
      newDeliveryName: '',
      deliveryTrackingId: '',
      isLoading: false,
      selectedIds: [],
      card: props.card,
      masterShipment: props.masterShipment,
    });

    const qtyShortCols = {
      defaultSortField: 'name',
      defaultSortOrder: 'asc',
      rearrangeCols: false,
      isCheckable: false,
      isDetailed: false,
      customRowKey: 'uid',
      fields: [{
        name: 'field-generic-input',
        prop: 'name',
        width: '400px',
        title: 'Item',
        id: 'name',
      },
      {
        name: 'field-generic-input',
        prop: 'catId',
        width: '112px',
        title: 'Catalog ID',
        id: 'catId',
      },
       {
        name: 'field-generic-input',
        prop: props.shortType === 'received' ? 'qtyShort' : 'qtyShortLoad',
        width: '112px',
        title: 'QTY Available',
        id: 'available',
      },
      {
        name: 'field-generic-input',
        prop: props.shortType === 'received' ? 'qtyShort' : 'qtyShortLoad',
        width: '120px',
        title: 'Qty To Ship',
        id: 'available',
      }],
    };
    const  tableProps = props.shortType ? qtyShortCols : tableDefinition;
    //const deliveries = computed(() => state.masterShipment?.partialShipments || []);

    const loadData = () => {
      if (['received', 'load'].includes(props.shortType)) return props.qtyShortItem;
      const fetchedData = state.masterShipment?.items || [];
      fetchedData.forEach((item) => {
        if (item && !item.selectedQty) {
          item.selectedQty = 0;
        }
      });
      if (fetchedData.length > 0 && state.searchText) {
        fetchedData = filter(
          fetchedData,
          (item) =>
            item?.name &&
            item.name.toLowerCase().includes(state.searchText.toLowerCase())
        );
      }
      state.selectedItems = [];
      return fetchedData;
    };

    const selectedRows = (rows) => {
      state.selectedItems = rows;
      rows.forEach((row) => {
        if (!row.selectedQty) row.selectedQty = row.quantity || 0;
      });
    };

    const itemQtyChanged = (newQty, item) => {
      item.selectedQty = newQty;
      if (newQty > 0) {
        if (!state.selectedIds.includes(item._id)) {
          state.selectedIds.push(item._id);
        }
      } else {
        state.selectedIds = state.selectedIds.filter((id) => id !== item._id);
      }
    };

    const maxQtyAllowed = (item) => item.quantity;

    const addItemsFromMaster = () => {
      const clonedItems = cloneDeep(state.selectedItems);
      const itemsToAdd = [];
      for (const item of clonedItems) {
        if (!item.selectedQty || item.selectedQty <= 0) continue;
        Object.assign(item, {
          quantity: item.selectedQty,
          selectedQty: item.selectedQty,
          isUsed: true,
          id: `${item._id}_${state.masterShipment?._id || ''}`,
          isMasterItem: true,
          selected: true,
          showInput: true,
          isEditing: true,
          isTemporaryAddition: true,
        });

        itemsToAdd.push(item);
      }
      return itemsToAdd;
    };
    const createDelivery = async () => {
      state.isLoading = true;
      const itemsToBeAdded = addItemsFromMaster();
      if (!itemsToBeAdded.length) {
        toast.error("Please select at least one item to add");
        state.isLoading = false;
        return;
      }
      let targetDelivery;
      let addedCount = 0;

      try {
        if (state.deliveryType === "existing") {
          const selectedId = state.selectedDelivery?._id || state.selectedDelivery;
          targetDelivery = state.masterShipment.partialShipments.find(
            (p) => p._id === selectedId
          );

          if (!targetDelivery) {
            toast.error("No delivery selected");
            state.isLoading = false;
            return;
          }
          if (!targetDelivery._newItems) {
            targetDelivery._newItems = { fromShipments: [] };
          }

          for (const item of itemsToBeAdded) {
            if (item.selected && item.isMasterItem) {
              targetDelivery.addItemFromShipment(
                item,
                state.masterShipment,
                item.selectedQty
              );
              addedCount++;
            }
          }

          await Shipping.splitFromLabels({
            projectId: targetDelivery.project?._id,
            toShippingId: targetDelivery._id,
            items: targetDelivery._newItems.fromShipments,
          });

          toast.success("Updated existing delivery");
        } else {
          const emptyRow = AddRow.createEmptyRow(tableProps);
          state.card._delivery.trackingId = state.deliveryTrackingId;
          state.deliveryTrackingId = state.card._delivery.trackingId;
          const master = state.masterShipment;
          const deliveryObj = {
            deliverBy: state.card._delivery?.deliverBy,
            deliveryStart: state.card._delivery?.deliveryStart,
            trackingId: state.card._delivery?.trackingId,
            deliveryLocation: master._delivery.deliveryLocation,
            currentLocation: master._delivery.currentLocation,
            deliveryProject: master.project,
            currentProject: master._delivery.currentProject,
            recipient: master._delivery?.recipient || master.createdBy,
            owner: master._delivery?.owner || master.createdBy,
          };
          const newPartial = new ShippingLabel({
            ...emptyRow,
            delivery: deliveryObj,
            currentLocation: deliveryObj.currentLocation,
            name: state.newDeliveryName,
            status: "not-started",
            project: state.masterShipment.project,
            trackingId: state.card._delivery.trackingId,
            parentShipment: state.masterShipment?._id,
            shipType: "s",
            fromSchedule: true,
          });
          newPartial.files = [];
          for (const item of itemsToBeAdded) {
            if (item.selected && item.isMasterItem) {
              newPartial.addItemFromShipment(
                item,
                state.masterShipment,
                item.selectedQty
              );
              addedCount++;
            }
          }
          await newPartial.save();
          toast.success("Created delivery successfully");
        }
        emit("close");
        
      } catch (e) {
        console.error("Failed to process delivery", e);
      } finally {
        state.isLoading = false;
      }
    };
    // const unscheduledItems = computed(() =>
    //   (state.card.items || []).filter(item => {
    //     return item.masterSummary?.notScheduled > 0;
    //   })
    // );

    const deliveries = computed(() => {
      const partials = get(props, 'masterShipment.partialShipments', []);
      const validPartials = partials.filter((p) => ['not-started', 'un-scheduled'].includes(p.status));
      return validPartials.map((p) => ({ _id: p._id,name: p.name,}));
    });

    const isDisabled = () => {
      const isValidDate = (d) => !!d && !isNaN(new Date(d).getTime());
      const delivery = state.card._delivery || {};
      state.card._delivery = delivery;

      if (['received', 'load'].includes(props.shortType)  && state.deliveryType === 'existing') {
        return !state.selectedDelivery;
      }

      if (!props.shortType) {
        const isNoItemsSelected = isEmpty(state.selectedItems);
        if (isNoItemsSelected) return true;

        const hasInvalidQty = state.selectedItems.some(item => item.selectedQty === 0);
        if (hasInvalidQty) return true;

        if (state.deliveryType === 'existing') {
          return isNoItemsSelected || hasInvalidQty || !state.selectedDelivery;
        }
      }
      if (!isValidDate(delivery.deliverBy) || !isValidDate(delivery.deliveryStart)) return true;
      const trimmedName = state.newDeliveryName.trim() || '';
      if (trimmedName.length < 3) return true;

      return false;
    };

    const createShortDelivery = async () => {
      try {
        state.isLoading = true;
        const params = {};
        if (state.deliveryType === 'existing') {
          params.existingSlId = '';
          params.existingSlId =state.selectedDelivery?._id || state.selectedDelivery;
        } else {
          params.name = state.newDeliveryName;
        }
        params.deliverBy = {
          kind: 'deliverBy',
          val: get(state.card, '_delivery.deliverBy'),
        }
        params.deliveryStart = {
          'kind': 'deliveryStart',
          val: get(state.card, '_delivery.deliveryStart'),
        }
        params.itemId = get(props.qtyShortItem[0], '_id');
        params.parentShipmentId = props.masterShipment._id
        params.type = props.shortType;
        if (props.masterShipment.shipType === 's-m') {
          let resMasterLabel =await Shipping.breakShipmentIntoPartials({
            shippingLabelId: props.masterShipment._id,
            deliveryName: props.masterShipment.name
          });
          params.parentShipmentId = resMasterLabel._id
        }
        await Shipping.pickShortDelivery(params)
        if (props.masterShipment.shipType === 's-m') {
          router.replace({ path: `/shipping/${props.masterShipment.project._id}/${params.parentShipmentId}`});
        }
        emit("close");
      } catch (e) {
        console.error('Error: ', e);
      } finally {
        state.isLoading = false;
      }
    }

    return {
      ...toRefs(state),
      tableProps,
      deliveries,
      loadData,
      selectedRows,
      createDelivery,
      deliveryItemsTable,
      itemQtyChanged,
      maxQtyAllowed,
      isDisabled,
      createShortDelivery
    };
  },
});
</script>

<style scoped>
  .modal-card-head {
    background-color: #fff !important;
    border-bottom: none !important;
  }
  .modal-card-foot {
    border-top: 1px solid #84888c;
     background-color: #fff !important;
  }
</style>
